/*
 * Open a shop page in a popup instead of navigating to it.
 *
 * Any link with data-pb-page-modal gets this: the click fetches the link's own href, lifts the
 * page title and content out of the response and shows them in a native <dialog>. The link stays
 * a normal link, so a modified click (new tab), a failed request or a page without recognisable
 * content all fall back to plain navigation.
 *
 * Optional data-close-label on the link sets the close button's accessible name.
 */
(() => {
  if (window.pbPageModalBound) return;
  window.pbPageModalBound = true;

  const cache = new Map();
  let dialog;

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'pb-page-modal';
    dialog.innerHTML =
      '<div class="pb-page-modal__panel">' +
      '<button type="button" class="pb-page-modal__close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg></button>' +
      '<h2 class="pb-page-modal__title" tabindex="-1"></h2>' +
      '<div class="pb-page-modal__body rte"></div>' +
      '</div>';
    dialog.querySelector('.pb-page-modal__close').addEventListener('click', () => dialog.close());
    // A click on the backdrop lands on the dialog element itself, not on the panel inside it.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => document.documentElement.classList.remove('pb-page-modal-open'));
    document.body.appendChild(dialog);
  }

  async function load(url) {
    if (cache.has(url)) return cache.get(url);
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const main = doc.querySelector('main');
    const body = main?.querySelector('.rte');
    if (!body || !body.textContent.trim()) throw new Error('no page content');
    body.querySelectorAll('script, style, iframe').forEach((node) => node.remove());
    const page = { title: main.querySelector('h1')?.textContent.trim() || '', html: body.innerHTML };
    cache.set(url, page);
    return page;
  }

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('a[data-pb-page-modal]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof HTMLDialogElement !== 'function') return;

    event.preventDefault();
    if (!dialog) build();

    const title = dialog.querySelector('.pb-page-modal__title');
    const body = dialog.querySelector('.pb-page-modal__body');
    dialog.querySelector('.pb-page-modal__close').setAttribute('aria-label', link.dataset.closeLabel || 'Close');
    title.textContent = '';
    body.innerHTML = '';
    dialog.classList.add('pb-page-modal--loading');
    document.documentElement.classList.add('pb-page-modal-open');
    dialog.showModal();

    try {
      const page = await load(link.href);
      title.textContent = page.title;
      body.innerHTML = page.html;
      dialog.classList.remove('pb-page-modal--loading');
      dialog.querySelector('.pb-page-modal__panel').scrollTop = 0;
      title.focus({ preventScroll: true });
    } catch (error) {
      dialog.close();
      window.location.href = link.href;
    }
  });
})();
